import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DASHBOARD_GRAPH_OPTIONS, getGraphOptionColumns } from "./dashboardGraphCatalog";
import "./DashboardGraphSection.scss";

const MAX_INLINE_CHIPS = 10;

const allSelectedSet = () => new Set(DASHBOARD_GRAPH_OPTIONS.map((o) => o.id));

function orderedSelectedIds(selectedSet) {
  return DASHBOARD_GRAPH_OPTIONS.filter((o) => selectedSet.has(o.id)).map((o) => o.id);
}

const ChevronIcon = ({ up }) => (
  <svg
    className="pos-dashboard-graph__chevron-svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{ transform: up ? "rotate(180deg)" : "none" }}
  >
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseChipIcon = () => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M12 4L4 12M4 4L12 12" stroke="#A4A4A4" strokeWidth={1.2} strokeLinecap="round" />
  </svg>
);

/**
 * Controlled graph picker: `selectedIds` is owned by the parent (`visibleGraphIds`).
 *
 * @param {object} props
 * @param {Set<string>} props.selectedIds
 * @param {React.Dispatch<Set<string> | ((prev: Set<string>) => Set<string>)>} props.onSelectionChange
 */
function DashboardGraphSection({ selectedIds: selectedIdsProp, onSelectionChange }) {
  const { t } = useTranslation();
  const [panelOpen, setPanelOpen] = useState(false);
  const [draftIds, setDraftIds] = useState(allSelectedSet);

  const selectedIds = useMemo(() => {
    return selectedIdsProp instanceof Set
      ? selectedIdsProp
      : new Set(Array.isArray(selectedIdsProp) ? selectedIdsProp : []);
  }, [selectedIdsProp]);

  const emitSelection = useCallback(
    (next) => {
      if (typeof onSelectionChange !== "function") return;
      onSelectionChange(next);
    },
    [onSelectionChange],
  );

  const optionById = useMemo(() => {
    const m = new Map();
    DASHBOARD_GRAPH_OPTIONS.forEach((o) => m.set(o.id, o));
    return m;
  }, []);

  const columns = useMemo(() => getGraphOptionColumns(), []);

  const togglePanel = useCallback(() => {
    setPanelOpen((open) => {
      if (!open) {
        setDraftIds(new Set(selectedIds));
      }
      return !open;
    });
  }, [selectedIds]);

  /** Keeps panel draft in sync when selection changes elsewhere (e.g. chart card kebab Remove). */
  useEffect(() => {
    if (!panelOpen) return;
    setDraftIds((d) => {
      const next = new Set(d);
      for (const id of next) {
        if (!selectedIds.has(id)) next.delete(id);
      }
      return next;
    });
  }, [selectedIds, panelOpen]);

  const removeChip = useCallback(
    (id) => {
      emitSelection((prev) => {
        const base = prev instanceof Set ? prev : new Set(prev);
        const n = new Set(base);
        n.delete(id);
        return n;
      });
      if (panelOpen) {
        setDraftIds((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
      }
    },
    [emitSelection, panelOpen],
  );

  const toggleDraft = useCallback((id) => {
    setDraftIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const applyDraft = useCallback(() => {
    emitSelection(new Set(draftIds));
    setPanelOpen(false);
  }, [draftIds, emitSelection]);

  const clearDraft = useCallback(() => {
    setDraftIds(new Set());
  }, []);

  const ordered = useMemo(() => orderedSelectedIds(selectedIds), [selectedIds]);
  const showOverflow = ordered.length > MAX_INLINE_CHIPS;
  const idsInline = showOverflow ? ordered.slice(0, MAX_INLINE_CHIPS) : ordered;
  const overflowCount = showOverflow ? ordered.length - MAX_INLINE_CHIPS : 0;

  const labelFor = (opt) => t(opt.labelKey, { defaultValue: opt.defaultLabel });

  return (
    <div className="pos-dashboard-graph">
      <div className="pos-dashboard-graph__bar">
        <div className="pos-dashboard-graph__label">{t("Graph", { defaultValue: "Graph" })}</div>
        <div className={`pos-dashboard-graph__chips ${panelOpen ? "pos-dashboard-graph__chips--wrap" : ""}`}>
          {(panelOpen ? ordered : idsInline).map((id) => {
            const opt = optionById.get(id);
            if (!opt) return null;
            return (
              <span key={id} className="pos-dashboard-graph__chip">
                <span className="pos-dashboard-graph__chip-inner">
                  <span className="pos-dashboard-graph__chip-label">{labelFor(opt)}</span>
                  <button
                    type="button"
                    className="pos-dashboard-graph__chip-remove"
                    aria-label={t("Remove", { defaultValue: "Remove" })}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeChip(id);
                    }}
                  >
                    <CloseChipIcon />
                  </button>
                </span>
              </span>
            );
          })}
          {!panelOpen && overflowCount > 0 && (
            <span className="pos-dashboard-graph__chip pos-dashboard-graph__chip--overflow">
              <span className="pos-dashboard-graph__chip-inner">
                <span className="pos-dashboard-graph__chip-label">
                  + {overflowCount} …
                </span>
              </span>
            </span>
          )}
        </div>
        <button
          type="button"
          className="pos-dashboard-graph__toggle"
          aria-expanded={panelOpen}
          aria-label={panelOpen ? t("Close graph picker", { defaultValue: "Close graph picker" }) : t("Open graph picker", { defaultValue: "Open graph picker" })}
          onClick={togglePanel}
        >
          <ChevronIcon up={panelOpen} />
        </button>
      </div>

      {panelOpen && (
        <div className="pos-dashboard-graph__panel">
          <div className="pos-dashboard-graph__matrix">
            <div className="pos-dashboard-graph__matrix-cols">
              {columns.map((col, colIdx) => (
                <div key={`graph-col-${colIdx}`} className="pos-dashboard-graph__col">
                  {col.map((opt) => {
                    const checked = draftIds.has(opt.id);
                    return (
                      <label key={opt.id} className="pos-dashboard-graph__check-row">
                        <span className="pos-dashboard-graph__checkbox-wrap">
                          <input
                            type="checkbox"
                            className="pos-dashboard-graph__checkbox-input"
                            checked={checked}
                            onChange={() => toggleDraft(opt.id)}
                          />
                          <span className="pos-dashboard-graph__checkbox-ui" aria-hidden />
                        </span>
                        <span className="pos-dashboard-graph__check-label">{labelFor(opt)}</span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="pos-dashboard-graph__actions">
              <button type="button" className="pos-dashboard-graph__btn pos-dashboard-graph__btn--apply" onClick={applyDraft}>
                {t("Apply", { defaultValue: "Apply" })}
              </button>
              <button type="button" className="pos-dashboard-graph__btn pos-dashboard-graph__btn--clear" onClick={clearDraft}>
                {t("Clear", { defaultValue: "Clear" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardGraphSection;
